import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Divider,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Stack,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Info as InfoIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Circle as CircleIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format, isToday, isTomorrow, isYesterday, isPast } from 'date-fns';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useContentResponsive } from '@/contexts/ContentSpaceContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import {
  Task,
  TaskFilters,
  TaskGrouping,
  TaskStatus,
  TaskType,
  TaskPriority
} from '@/types/taskTypes';
import { TaskService } from '@/services/taskService';
import { CreateTaskDialog } from '@/components/CreateTaskDialog/CreateTaskDialog';
import { TaskFiltersDialog } from '@/components/TaskFiltersDialog/TaskFiltersDialog';
import {
  SortIconDropdown,
  SortOption
} from '@/components/SortIconDropdown/SortIconDropdown';
import { CheckSquare } from '@phosphor-icons/react';

const TasksPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { shouldStackVertically } = useContentResponsive();
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<TaskFilters>({});
  const [grouping, setGrouping] = useState<TaskGrouping>({
    field: 'dueDate',
    order: 'asc'
  });
  const [showCompleted, setShowCompleted] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<string>('dueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskMenuAnchorEl, setTaskMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );

  // Sort options for the dropdown
  const sortOptions: SortOption[] = [
    { key: 'dueDate', label: 'Due Date' },
    { key: 'priority', label: 'Priority' },
    { key: 'status', label: 'Status' },
    { key: 'assignee', label: 'Assignee' },
    { key: 'type', label: 'Type' },
    { key: 'createdDate', label: 'Created Date' }
  ];

  // Load tasks
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const currentFilters = {
        ...filters,
        search: searchQuery || undefined,
        status: showCompleted
          ? undefined
          : [TaskStatus.Pending, TaskStatus.InProgress, TaskStatus.Overdue]
      };

      const response = await TaskService.getTasks(
        currentFilters,
        grouping,
        1,
        50,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setTasks(response.tasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, grouping, searchQuery, showCompleted]);

  useEffect(() => {
    loadTasks();
  }, [filters, grouping, searchQuery, showCompleted]);

  // Event listeners for real-time updates
  useEffect(() => {
    const handleTaskCreated = () => loadTasks();
    const handleTaskUpdated = () => loadTasks();
    const handleTaskDeleted = () => loadTasks();

    window.addEventListener('taskCreated', handleTaskCreated);
    window.addEventListener('taskUpdated', handleTaskUpdated);
    window.addEventListener('taskDeleted', handleTaskDeleted);

    return () => {
      window.removeEventListener('taskCreated', handleTaskCreated);
      window.removeEventListener('taskUpdated', handleTaskUpdated);
      window.removeEventListener('taskDeleted', handleTaskDeleted);
    };
  }, []);

  // Handlers
  const handleCreateTask = () => {
    setCreateDialogOpen(true);
  };

  const handleTaskComplete = async (task: Task) => {
    try {
      await TaskService.updateTask({
        id: task.id,
        status:
          task.status === TaskStatus.Completed
            ? TaskStatus.Pending
            : TaskStatus.Completed
      });
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleTaskMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    task: Task
  ) => {
    event.stopPropagation();
    setSelectedTask(task);
    setTaskMenuAnchorEl(event.currentTarget);
  };

  const handleTaskMenuClose = () => {
    setTaskMenuAnchorEl(null);
    setSelectedTask(null);
  };

  // Helper functions
  const getTaskTypeIcon = (type: TaskType) => {
    switch (type) {
      case TaskType.Approval:
        return <CheckCircleIcon fontSize="small" />;
      case TaskType.Review:
        return <AssignmentIcon fontSize="small" />;
      case TaskType.FollowUp:
        return <ScheduleIcon fontSize="small" />;
      case TaskType.Information:
        return <InfoIcon fontSize="small" />;
      default:
        return <CircleIcon fontSize="small" />;
    }
  };

  const getTaskStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.Completed:
        return theme.palette.success.main;
      case TaskStatus.InProgress:
        return theme.palette.info.main;
      case TaskStatus.Overdue:
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.Critical:
        return theme.palette.error.main;
      case TaskPriority.High:
        return theme.palette.warning.main;
      case TaskPriority.Medium:
        return theme.palette.info.main;
      case TaskPriority.Low:
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const formatDueDate = (dueDate: string) => {
    const date = new Date(dueDate);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d');
  };

  const isDueSoon = (dueDate: string) => {
    const date = new Date(dueDate);
    const now = new Date();
    const diffHours = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
    return diffHours > 0 && diffHours <= 24;
  };

  // Group tasks
  const groupedTasks = tasks.reduce((groups, task) => {
    let groupKey: string;

    switch (grouping.field) {
      case 'status':
        groupKey = task.status;
        break;
      case 'assignee':
        groupKey = task.assignedTo?.name || 'Unassigned';
        break;
      case 'dueDate':
        if (!task.dueDate) {
          groupKey = 'No due date';
        } else {
          const date = new Date(task.dueDate);
          if (isPast(date) && !isToday(date)) {
            groupKey = 'Overdue';
          } else if (isToday(date)) {
            groupKey = 'Today';
          } else if (isTomorrow(date)) {
            groupKey = 'Tomorrow';
          } else {
            groupKey = format(date, 'MMM d, yyyy');
          }
        }
        break;
      case 'priority':
        groupKey = task.priority;
        break;
      case 'type':
        groupKey = task.type;
        break;
      case 'createdDate':
        groupKey = format(new Date(task.createdAt), 'MMM d, yyyy');
        break;
      default:
        groupKey = 'All Tasks';
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(task);
    return groups;
  }, {} as Record<string, Task[]>);

  // Sort groups to ensure consistent ordering (Overdue, Today, Tomorrow, then dates)
  const sortedGroupEntries = Object.entries(groupedTasks).sort(([a], [b]) => {
    const order = ['Overdue', 'Today', 'Tomorrow'];
    const aIndex = order.indexOf(a);
    const bIndex = order.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // For other groups, sort alphabetically
    return a.localeCompare(b);
  });

  const activeTasksCount = tasks.filter(
    (task) =>
      task.status !== TaskStatus.Completed &&
      task.status !== TaskStatus.Cancelled
  ).length;

  // Set header text and actions
  useEffect(() => {
    setHeaderText(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CheckSquare size={16} weight="regular" />
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Tasks
        </Typography>
      </Box>
    );

    setMenuActions(
      isMobile ? (
        <IconButton
          onClick={handleCreateTask}
          sx={{
            backgroundColor: '#e6f226',
            color: '#030766',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#d4e022'
            }
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTask}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: '#e6f226',
            color: '#030766',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#d4e022',
              boxShadow: 'none'
            }
          }}
        >
          New Task
        </Button>
      )
    );

    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [theme.palette.mode, isMobile, handleCreateTask]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        minHeight: 0
      }}
    >
      {/* Search and Controls Section */}
      <Box
        sx={{
          position: 'sticky',
          top: isMobile ? '56px' : '64px',
          zIndex: 10,
          background: theme.palette.background.paper,
          px: 4,
          pt: 3,
          pb: 0
        }}
      >
        {/* Search and Sort Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2
          }}
        >
          <TextField
            placeholder="Search tasks..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                height: 40,
                '&:hover': {
                  borderColor: '#d1d5db'
                },
                '&.Mui-focused': {
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9ca3af', fontSize: '18px' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <SortIconDropdown
            options={sortOptions}
            currentSort={sortBy}
            currentDirection={sortDir}
            onSortChange={(key, direction) => {
              if (key && direction) {
                setSortBy(key);
                setSortDir(direction);
                setGrouping({ field: key as any, order: direction });
              }
            }}
            size={isMobile ? 'medium' : 'small'}
          />

          <Button
            onClick={() => {
              setShowFilters(!showFilters);
              if (!showFilters) {
                // Reset filters when showing
                setShowCompleted(false);
              } else {
                // Reset when hiding
                setShowCompleted(false);
              }
            }}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              height: 40,
              width: 40,
              minWidth: 40,
              px: 0,
              border: '1px solid #e1e5e9',
              backgroundColor: showFilters
                ? 'rgba(75, 94, 219, 0.04)'
                : 'white',
              color: showFilters ? '#4B5EDB' : '#6b7280',
              '&:hover': {
                backgroundColor: showFilters
                  ? 'rgba(75, 94, 219, 0.08)'
                  : '#f8f9fa',
                borderColor: showFilters ? '#4B5EDB' : '#d1d5db'
              }
            }}
          >
            <FilterIcon sx={{ fontSize: '18px' }} />
          </Button>
        </Box>

        {/* Filter Pills - Only show when filters are enabled */}
        {showFilters && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              onClick={() => setShowCompleted(false)}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                height: 32,
                px: 2,
                border: '1px solid #e1e5e9',
                backgroundColor: !showCompleted
                  ? 'rgba(75, 94, 219, 0.04)'
                  : 'white',
                color: !showCompleted ? '#4B5EDB' : '#6b7280',
                '&:hover': {
                  backgroundColor: !showCompleted
                    ? 'rgba(75, 94, 219, 0.08)'
                    : '#f8f9fa',
                  borderColor: !showCompleted ? '#4B5EDB' : '#d1d5db'
                }
              }}
            >
              Active ({activeTasksCount})
            </Button>
            <Button
              onClick={() => setShowCompleted(true)}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                height: 32,
                px: 2,
                border: '1px solid #e1e5e9',
                backgroundColor: showCompleted
                  ? 'rgba(75, 94, 219, 0.04)'
                  : 'white',
                color: showCompleted ? '#4B5EDB' : '#6b7280',
                '&:hover': {
                  backgroundColor: showCompleted
                    ? 'rgba(75, 94, 219, 0.08)'
                    : '#f8f9fa',
                  borderColor: showCompleted ? '#4B5EDB' : '#d1d5db'
                }
              }}
            >
              All ({tasks.length})
            </Button>
          </Box>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 0 }} />}
      </Box>

      {/* Content Area */}
      <Box
        sx={{
          px: 4,
          py: 2,
          backgroundColor: theme.palette.background.paper
        }}
      >
        {sortedGroupEntries.map(([groupName, groupTasks], index) => (
          <Box key={groupName} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1.5,
                mt: index > 0 ? 2 : 0, // Add top margin for spacing between groups
                color: 'text.secondary',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: 'left'
              }}
            >
              {groupName} ({groupTasks.length})
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {groupTasks.map((task) => (
                <Card
                  key={task.id}
                  sx={{
                    cursor: task.relatedRecord?.url ? 'pointer' : 'default',
                    transition: 'all 0.15s ease',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    borderRadius: '6px',
                    boxShadow: 'none',
                    overflow: 'hidden',
                    '&:hover': task.relatedRecord?.url
                      ? {
                          borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fafafa'
                        }
                      : {
                          borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fafafa'
                        }
                  }}
                  onClick={() =>
                    task.relatedRecord?.url &&
                    window.open(task.relatedRecord.url, '_blank')
                  }
                >
                  <CardContent
                    sx={{ 
                      p: 2,
                      '&:last-child': { pb: 2 } 
                    }}
                  >
                    {/* Table-like layout */}
                    <Box
                      sx={{
                        display: { xs: 'flex', sm: 'grid' },
                        flexDirection: { xs: 'row', sm: 'unset' },
                        gridTemplateColumns: {
                          sm: '2fr 1fr 1fr 1fr auto'
                        },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 2,
                        minHeight: { xs: 'auto', sm: 40 }
                      }}
                    >
                      {/* Task Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        <Tooltip
                          title={
                            task.status === TaskStatus.Completed
                              ? 'Mark incomplete'
                              : 'Mark complete'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskComplete(task);
                            }}
                            sx={{
                              color:
                                task.status === TaskStatus.Completed
                                  ? 'success.main'
                                  : 'action.disabled',
                              '&:hover': {
                                backgroundColor:
                                  task.status === TaskStatus.Completed
                                    ? 'success.light'
                                    : 'action.hover'
                              },
                              p: 0.25,
                              flexShrink: 0,
                              mt: { xs: 0.25, sm: 0 }
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          {/* Title Row */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.25
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                textDecoration:
                                  task.status === TaskStatus.Completed
                                    ? 'line-through'
                                    : 'none',
                                opacity:
                                  task.status === TaskStatus.Completed
                                    ? 0.7
                                    : 1,
                                color: 'text.primary',
                                textAlign: 'left',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {task.title}
                            </Typography>

                            {/* Priority Indicator */}
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: getPriorityColor(
                                  task.priority
                                ),
                                flexShrink: 0
                              }}
                            />
                          </Box>

                          {/* Metadata Row - Mobile only */}
                          <Typography
                            sx={{
                              display: { xs: 'block', sm: 'none' },
                              fontSize: '12px',
                              color: 'text.secondary',
                              textAlign: 'left'
                            }}
                          >
                            {task.dueDate && (
                              <Box
                                component="span"
                                sx={{
                                  color: isPast(new Date(task.dueDate))
                                    ? 'error.main'
                                    : isDueSoon(task.dueDate)
                                    ? 'warning.main'
                                    : 'text.secondary'
                                }}
                              >
                                {formatDueDate(task.dueDate)}
                              </Box>
                            )}
                            {task.dueDate && task.assignedTo && ' • '}
                            {task.assignedTo && task.assignedTo.name}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Due Date Column - Desktop only */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        {task.dueDate ? (
                          <>
                            <ScheduleIcon
                              sx={{
                                fontSize: 14,
                                color: isPast(new Date(task.dueDate))
                                  ? 'error.main'
                                  : isDueSoon(task.dueDate)
                                  ? 'warning.main'
                                  : 'text.secondary'
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '13px',
                                color: isPast(new Date(task.dueDate))
                                  ? 'error.main'
                                  : isDueSoon(task.dueDate)
                                  ? 'warning.main'
                                  : 'text.secondary'
                              }}
                            >
                              Due {formatDueDate(task.dueDate)}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            sx={{ fontSize: '13px' }}
                          >
                            No due date
                          </Typography>
                        )}
                      </Box>

                      {/* Record Column - Desktop only */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        {task.relatedRecord ? (
                          <>
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: 'text.secondary',
                                flexShrink: 0
                              }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '13px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {task.relatedRecord.title}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            sx={{ fontSize: '13px' }}
                          >
                            No record
                          </Typography>
                        )}
                      </Box>

                      {/* Assigned To Column - Desktop only */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        {task.assignedTo ? (
                          <>
                            <Avatar
                              sx={{ width: 20, height: 20, fontSize: '11px' }}
                            >
                              {task.assignedTo.name.charAt(0)}
                            </Avatar>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '13px' }}
                            >
                              {task.assignedTo.name}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            sx={{ fontSize: '13px' }}
                          >
                            Unassigned
                          </Typography>
                        )}
                      </Box>

                      {/* Actions Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: 0.5,
                          flexShrink: 0
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleTaskMenuOpen(e, task)}
                          sx={{
                            borderRadius: '6px',
                            color: theme.palette.text.secondary,
                            mt: { xs: 0.25, sm: 0 },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                              color: theme.palette.text.primary
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        ))}

        {/* Empty State */}
        {!loading && tasks.length === 0 && (
          <Box
            sx={{
              py: 4,
              px: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <AssignmentIcon
                sx={{ fontSize: 24, color: 'text.disabled', mt: 0.5 }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  color="text.primary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  No tasks found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.5 }}
                >
                  {searchQuery || Object.keys(filters).length > 0
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : 'Create your first task to get started with task management.'}
                </Typography>
                {!searchQuery && Object.keys(filters).length === 0 && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateTask}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000',
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 4px 12px rgba(59, 130, 246, 0.4)' 
                          : undefined
                      }
                    }}
                  >
                    Create Task
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Task Menu */}
      <Menu
        anchorEl={taskMenuAnchorEl}
        open={Boolean(taskMenuAnchorEl)}
        onClose={handleTaskMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 4px 20px rgba(0, 0, 0, 0.5)' 
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`
          }
        }}
      >
        <MenuItem 
          onClick={handleTaskMenuClose}
          sx={{
            py: 1.25,
            px: 2,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Edit Task
          </ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleTaskMenuClose}
          sx={{
            py: 1.25,
            px: 2,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Duplicate Task
          </ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={handleTaskMenuClose} 
          sx={{ 
            py: 1.25,
            px: 2,
            color: 'error.main',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)'
            }
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Delete Task
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <CreateTaskDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      <TaskFiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
      />
    </Box>
  );
};

export default TasksPage;
